import React, { ChangeEvent, useContext } from 'react';
import { Link } from 'react-router-dom';

// Context
import { HostContext } from '../../../context/HostContext';
import { InstanceContext } from '../../../context/InstanceContext';
import { EventTypeContext } from '../../../context/EventTypeContext';

// Hooks
import { useInstanceForm } from '../../../hooks/useInstanceForm';

// Components
import Alert from '../../../component/Alert';
import ThirdPartyLinks from "../../../component/instance/ThirdPartyLinks";
import ConditionalRender from "../../../helpers/ConditionalRender";
import Tagging from "../../../component/instance/Tagging/Tagging";
import ReactTooltip from "react-tooltip";

// Form Components
import {
    TextInput,
    SelectField,
    TextAreaField,
    CheckboxField,
    PasswordField
} from '../../../components/forms';

// Constants
import { batchIntervalOptions } from '../../../component/Constants';
import { InstanceFormUtils } from '../../../helpers/InstanceFormValidation';

function Detail() {
    const { instances, setInstances } = useContext(InstanceContext);
    const { hosts } = useContext(HostContext);
    const { eventTypes } = useContext(EventTypeContext);

    const {
        formState,
        action,
        updateFormData,
        validateName,
        validateHost,
        validatePort,
        handleSubmit,
        requireDatabase,
        requireServer,
        hasStatisticsCollection,
        canViewPassword,
        getDefaultCollectBlockingSessions
    } = useInstanceForm({ instances, setInstances, eventTypes });

    const { formData, validation, uiState, dependencies } = formState;

    // Handle password change with special logic
    const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!formData.passwordChanged) {
            // Just want the character entered.
            updateFormData({
                password: (e.nativeEvent as any).data || '',
                passwordChanged: true
            });
        } else {
            updateFormData({ password: e.target.value });
        }
    };

    // Handle host mapping
    const handleHostMapping = (hostId: number) => {
        let updatedMappedHosts = [...formData.mappedHosts];
        const match = updatedMappedHosts.find(id => id === hostId);

        if (!match) {
            updatedMappedHosts.push(hostId);
        } else {
            updatedMappedHosts = updatedMappedHosts.filter(id => id !== hostId);
        }

        updateFormData({ mappedHosts: updatedMappedHosts });
    };

    const clearFilter = () => {
        updateFormData({ filter: '' });
    };

    // Ref for hidden button to trigger modal
    const modalTriggerRef = React.useRef<HTMLButtonElement>(null);

    // Handle Advanced Mode toggle with confirmation when disabling
    const handleAdvancedModeChange = (checked: boolean) => {
        if (!checked && formData.advanced) {
            // User is trying to disable Advanced mode - show confirmation modal
            modalTriggerRef.current?.click();
        } else {
            // Enabling Advanced mode - no confirmation needed
            updateFormData({ advanced: checked });
        }
    };

    // Confirm disabling Advanced mode
    const confirmDisableAdvanced = () => {
        updateFormData({ advanced: false });
    };

    // Track previous connection field values to detect user changes
    const prevFieldsRef = React.useRef<{
        type: string;
        host: string;
        port: number;
        server: string;
        database: string;
        initialized: boolean;
    }>({ type: '', host: '', port: 0, server: '', database: '', initialized: false });

    // Keep track of the current URL for replacements (updated via ref to avoid stale closures)
    const currentUrlRef = React.useRef(formData.url);
    React.useEffect(() => {
        currentUrlRef.current = formData.url;
    }, [formData.url]);

    // Update URL when connection details change
    React.useEffect(() => {
        const instanceDefaults = dependencies.defaultValues.find(item => item.type === formData.type);
        
        // Skip if defaults not loaded yet or missing required fields
        if (!instanceDefaults || !formData.host || !formData.port) {
            return;
        }

        const prev = prevFieldsRef.current;
        const isCreate = action.type === 'create';

        // For CREATE: always generate URL from template
        if (isCreate) {
            const dynamicUrl = InstanceFormUtils.buildDynamicUrl(
                instanceDefaults.urlformat || '',
                formData.host,
                formData.port,
                formData.server,
                formData.database
            );
            updateFormData({ url: dynamicUrl });
        } else if (prev.initialized) {
            // EDIT mode: check which fields changed and update URL accordingly
            const typeChanged = prev.type !== formData.type;
            const hostChanged = prev.host && prev.host !== formData.host;
            const portChanged = prev.port && prev.port !== formData.port;
            const serverChanged = prev.server && prev.server !== formData.server;
            const databaseChanged = prev.database && prev.database !== formData.database;

            // Only update URL if a connection field actually changed
            if (typeChanged || hostChanged || portChanged || serverChanged || databaseChanged) {
                let updatedUrl = currentUrlRef.current;

                // If type changed, regenerate from template (can't preserve custom URL across types)
                if (typeChanged) {
                    updatedUrl = InstanceFormUtils.buildDynamicUrl(
                        instanceDefaults.urlformat || '',
                        formData.host,
                        formData.port,
                        formData.server,
                        formData.database
                    );
                } else {
                    // Replace changed values in existing URL (preserves custom parts)
                    if (hostChanged) {
                        updatedUrl = updatedUrl.replace(prev.host, formData.host);
                    }
                    if (portChanged) {
                        // Replace port - try common patterns
                        updatedUrl = updatedUrl.replace(`:${prev.port}/`, `:${formData.port}/`);
                        updatedUrl = updatedUrl.replace(`:${prev.port};`, `:${formData.port};`);
                        // Handle port at end of string
                        if (updatedUrl.endsWith(`:${prev.port}`)) {
                            updatedUrl = updatedUrl.slice(0, -String(prev.port).length) + formData.port;
                        }
                    }
                    if (serverChanged) {
                        updatedUrl = updatedUrl.replace(prev.server, formData.server);
                    }
                    if (databaseChanged) {
                        updatedUrl = updatedUrl.replace(prev.database, formData.database);
                    }
                }

                updateFormData({ url: updatedUrl });
            }
        }

        // Update previous values for next comparison
        prevFieldsRef.current = {
            type: formData.type,
            host: formData.host,
            port: formData.port,
            server: formData.server,
            database: formData.database,
            initialized: true
        };

        // Rebuild ReactTooltip for changes
        ReactTooltip.rebuild();
    }, [formData.type, formData.host, formData.port, formData.server, formData.database, dependencies.defaultValues, updateFormData, action.type]);

    // Function to generate dynamic callout text based on instance type
    const getCalloutText = () => {
        const instanceTypeMapping: { [key: string]: string } = {
            'cockroachdb': 'cockroachdb',
            'db2': 'db2',
            'informix': 'informix',
            'mysql': 'mysql',
            'oracle': 'oracle',
            'postgresql': 'postgresql',
            'saphana': 'saphana',
            'sqlserver': 'sqlserver',
            'sybase': 'sybase',
            'mongodb': 'mongodb'
        };

        const instanceTypeNames: { [key: string]: string } = {
            'cockroachdb': 'CockroachDB',
            'db2': 'DB2',
            'informix': 'Informix',
            'mysql': 'MySQL',
            'oracle': 'Oracle',
            'postgresql': 'PostgreSQL',
            'saphana': 'SAP HANA',
            'sqlserver': 'SQL Server',
            'sybase': 'SAP (Sybase) ASE',
            'mongodb': 'MongoDB'
        };

        const baseText = 'For list of supported platforms see ';
        const urlSlug = instanceTypeMapping[formData.type];
        const typeName = instanceTypeNames[formData.type];

        if (urlSlug && typeName) {
            return (
                <>
                    {baseText}
                    <a href="https://docs.dbmarlin.com/docs/Getting-Started/supported-platforms" target="_blank" rel="noopener noreferrer">Supported Platforms</a>
                    . For {typeName} prerequisites, see <a href={`https://docs.dbmarlin.com/docs/Monitored-Technologies/Databases/${urlSlug}`} target="_blank" rel="noopener noreferrer">{typeName}</a>.
                </>
            );
        }

        return (
            <>
                {baseText}
                <a href="https://docs.dbmarlin.com/docs/Getting-Started/supported-platforms" target="_blank" rel="noopener noreferrer">Supported Platforms</a>
            </>
        );
    };

    // Set default port and reset advanced settings when type changes
    const previousTypeRef = React.useRef(formData.type);
    React.useEffect(() => {

        // Only update port if the type has actually changed (not on initial load)
        if (previousTypeRef.current !== formData.type) {
            const instanceDefaults = dependencies.defaultValues.find(item => item.type === formData.type);
            if (instanceDefaults) {
                // Reset advanced mode settings to defaults when instance type changes
            updateFormData({
                port: instanceDefaults.defaultport,
                collectSqlStatistics: true,
                collectBlockingSessions: getDefaultCollectBlockingSessions(formData.type),
                collectTransactionContention: false,
                collectDeadlocks: false,
                collectTempdb: false,
                collectJobHistory: false,
                objectStatistics: false,
                detectChanges: true,
                debug: false,
                monitoringInterval: 1000,
                explainLimit: 20,
                batchInterval: 60,
                parameters: undefined });
            }
            previousTypeRef.current = formData.type;
        }
    }, [formData.type, dependencies.defaultValues]);

    return (
        <>
            <div className="col">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-database fa-fw" aria-hidden="true"></i>
                        {action.title} instance
                    </div>
                    <div className="card-body">
                        {uiState.isMissing ? (
                                <React.Fragment>
                                    <Alert
                                            message="The requested instance is either invalid or no longer available."
                                            heading="Invalid request"
                                            variant="alert-danger"
                                    />
                                    <div className="row g-0 form">
                                        <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                            <Link to="/admin/instances/" role="button" className="btn btn-secondary">
                                                <i className="fal fa-arrow-circle-left fa-fw fa-sm"></i>
                                                <span>Back</span>
                                            </Link>
                                        </div>
                                    </div>
                                </React.Fragment>
                        ) : (
                                <React.Fragment>
                                    {/* Alerts */}
                                    {action.type === 'delete' && (
                                            <Alert
                                                    message="Please check and ensure you want to delete this instance. Any associated media will remain, and will need to be deleted separately."
                                                    heading="Deletes are final and cannot be undone"
                                                    variant="alert-warning"
                                            />
                                    )}

                                    {!validation.isFormValid && (
                                            <Alert
                                                    message="Please complete all the required fields below."
                                                    heading="Missing required fields"
                                                    variant="alert-danger"
                                            />
                                    )}

                                    {/* Callout for supported platforms - only show for create action */}
                                    {action.type === 'create' && (
                                        <div className="alert alert-info" role="alert">
                                            <i className="fal fa-info-circle fa-fw me-2" aria-hidden="true"></i>
                                            {getCalloutText()}
                                        </div>
                                    )}

                                    <p>Please complete the required details below:</p>

                                    <div className="row g-0 form">
                                        {/* Basic Instance Information */}
                                        <TextInput
                                                id="name"
                                                label="Name"
                                                value={formData.name}
                                                onChange={(value) => updateFormData({ name: value })}
                                                onBlur={validateName}
                                                required
                                                disabled={uiState.disabled}
                                                maxLength={250}
                                                tooltip="The instance name"
                                                error={validation.name.isUsed ? "Instance name already used." : undefined}
                                                className={validation.name.className}
                                                autoFocus
                                                dataLpIgnore
                                        />

                                        <TextInput
                                                id="host"
                                                label="Host Address"
                                                value={formData.host}
                                                onChange={(value) => updateFormData({ host: value })}
                                                onBlur={validateHost}
                                                required
                                                disabled={uiState.disabled}
                                                maxLength={250}
                                                tooltip="The host address"
                                                error={validation.host.isUsed ? "Host address already used." : undefined}
                                                className={validation.host.className}
                                                dataLpIgnore
                                        />

                                        <SelectField
                                                id="type"
                                                label="Instance Type"
                                                value={formData.type}
                                                onChange={(value) => updateFormData({ type: value as string })}
                                                options={dependencies.defaultValues.map((instanceType) => ({
                                                    value: instanceType.type,
                                                    label: instanceType.display
                                                }))}
                                                required
                                                disabled={uiState.disabled}
                                                placeholder="Select Instance Type"
                                                tooltip="Select the instance type"
                                                className={validation.type.className}
                                        />

                                        <TextInput
                                                id="port"
                                                label="Port"
                                                value={formData.port.toString()}
                                                onChange={(value) => {
                                                    const numericValue = InstanceFormUtils.sanitizeNumericInput(value);
                                                    updateFormData({ port: Number(numericValue) });
                                                }}
                                                onBlur={validatePort}
                                                required
                                                disabled={uiState.disabled}
                                                maxLength={5}
                                                tooltip="Set the connection port number"
                                                className={validation.port.className}
                                                dataLpIgnore
                                        />
                                        <div className="col-xs-12 col-md-6 col-lg-3">
                                            <small className="text-secondary">Port number must be 65,535 or lower.</small>
                                        </div>
                                        <div className="w-100"></div>

                                        {/* Database Name - Conditional */}
                                        {requireDatabase(formData.type) && (
                                                <TextInput
                                                        id="database"
                                                        label={formData.type === 'oracle' ? 'SID or Service Name' : 'Database'}
                                                        value={formData.database}
                                                        onChange={(value) => updateFormData({ database: value })}
                                                        disabled={uiState.disabled}
                                                        maxLength={250}
                                                        tooltip="Set the connection database"
                                                        dataLpIgnore
                                                />
                                        )}

                                        {/* Server Name - Conditional */}
                                        {requireServer(formData.type) && (
                                                <TextInput
                                                        id="server"
                                                        label="Server"
                                                        value={formData.server}
                                                        onChange={(value) => updateFormData({ server: value })}
                                                        disabled={uiState.disabled}
                                                        maxLength={250}
                                                        tooltip="Set the connection server"
                                                        dataLpIgnore
                                                />
                                        )}

                                        {/* Connection Credentials */}
                                        <TextInput
                                                id="username"
                                                label="Username"
                                                value={formData.username}
                                                onChange={(value) => updateFormData({ username: value })}
                                                disabled={uiState.disabled}
                                                maxLength={250}
                                                tooltip="Set the connection username"
                                                className={validation.username.className}
                                                autoComplete="new-username"
                                                name="realuser"
                                                dataLpIgnore
                                                ariaAutoComplete="none"
                                        />

                                        <PasswordField
                                                id="password"
                                                label="Password"
                                                value={formData.password}
                                                onChange={handlePasswordChange}
                                                disabled={uiState.disabled}
                                                maxLength={250}
                                                tooltip="Set the connection password"
                                                className={validation.password.className}
                                                canToggleVisibility={canViewPassword(formData.passwordChanged)}
                                                autoComplete="new-password"
                                                name="realpass"
                                                dataLpIgnore
                                                ariaAutoComplete="none"
                                                inputMode="none"
                                                list="autocompleteOff"
                                        />

                                        {/* Advanced Mode Toggle */}
                                        <CheckboxField
                                                id="advanced"
                                                label="Advanced Mode"
                                                checked={formData.advanced}
                                                onChange={handleAdvancedModeChange}
                                                disabled={uiState.disabled}
                                                tooltip="Enable advanced mode"
                                        />

                                        {/* Advanced Mode Fields */}
                                        {formData.advanced && (
                                                <div className="row g-0 form advanced-mod">
                                                    <TextAreaField
                                                            id="url"
                                                            label="Connection String"
                                                            value={formData.url}
                                                            onChange={(value) => updateFormData({ url: value })}
                                                            required
                                                            tooltip="Set the instance connection string"
                                                            className={validation.url.className}
                                                    />

                                                    <TextAreaField
                                                            id="parameters"
                                                            label="Connection Parameters"
                                                            value={formData.parameters || ''}
                                                            onChange={(value) => updateFormData({ parameters: value })}
                                                            tooltip="Set the instance parameters"
                                                    />

                                                    <SelectField
                                                            id="monitoring-interval"
                                                            label="Monitoring Interval (milliseconds)"
                                                            value={formData.monitoringInterval}
                                                            onChange={(value) => updateFormData({ monitoringInterval: Number(value) })}
                                                            options={[100, 200, 500, 1000, 2000, 5000, 10000, 20000, 30000, 60000].map(val => ({
                                                                value: val,
                                                                label: val.toString()
                                                            }))}
                                                            required
                                                            disabled={uiState.disabled}
                                                            tooltip="Set the monitoring interval"
                                                    />

                                                    <TextInput
                                                            id="explain-limit"
                                                            label="Explain Limit"
                                                            value={formData.explainLimit.toString()}
                                                            onChange={(value) => {
                                                                const numericValue = InstanceFormUtils.sanitizeNumericInput(value);
                                                                updateFormData({ explainLimit: Number(numericValue) });
                                                            }}
                                                            disabled={uiState.disabled}
                                                            maxLength={5}
                                                            tooltip="Set the explain limit"
                                                            dataLpIgnore
                                                    />

                                                    <SelectField
                                                            id="batch-interval"
                                                            label="Batch Interval"
                                                            value={formData.batchInterval}
                                                            onChange={(value) => updateFormData({ batchInterval: Number(value) })}
                                                            options={batchIntervalOptions.map(option => ({
                                                                value: option.value,
                                                                label: option.name
                                                            }))}
                                                            required
                                                            disabled={uiState.disabled}
                                                            tooltip="Set the batch interval"
                                                    />

                                                    {/* Conditional Collection Options */}
                                                    {hasStatisticsCollection(formData.type) && (
                                                            <>
                                                                <CheckboxField
                                                                        id="collect-sql-stats"
                                                                        label="Collect SQL Statistics"
                                                                        checked={formData.collectSqlStatistics}
                                                                        onChange={(checked) => updateFormData({ collectSqlStatistics: checked })}
                                                                        disabled={uiState.disabled}
                                                                />

                                                                {!formData.collectSqlStatistics && (
                                                                        <Alert
                                                                                message="SQL Statistics are important to the functioning of DBmarlin, and switching off may result in some loss of functionality. We therefore recommend collecting SQL Statistics."
                                                                                heading="Collect SQL Statistics disabled"
                                                                                variant="alert-warning"
                                                                        />
                                                                )}
                                                            </>
                                                    )}

                                                    {/* Blocking Sessions - Not for CockroachDB */}
                                                    <ConditionalRender if={formData.type !== 'cockroachdb'}>
                                                        <CheckboxField
                                                                id="collect-blocking-sessions"
                                                                label="Collect Blocking Sessions"
                                                                checked={formData.collectBlockingSessions}
                                                                onChange={(checked) => updateFormData({ collectBlockingSessions: checked })}
                                                                disabled={uiState.disabled}
                                                        />

                                                        <ConditionalRender if={!getDefaultCollectBlockingSessions(formData.type) && formData.collectBlockingSessions}>
                                                            <Alert
                                                                    message="Collecting Blocking Sessions will involve some additional overhead. We therefore suggest that you enable this feature when you need to investigate a problem, but do not leave switched on all the time"
                                                                    heading="Collect Blocking Sessions enabled"
                                                                    variant="alert-warning"
                                                            />
                                                        </ConditionalRender>
                                                    </ConditionalRender>

                                                    {/* Transaction Contention - Only for CockroachDB */}
                                                    <ConditionalRender if={formData.type === 'cockroachdb'}>
                                                        <CheckboxField
                                                                id="collect-transaction-contention"
                                                                label="Collect Transaction Contention"
                                                                checked={formData.collectTransactionContention}
                                                                onChange={(checked) => updateFormData({ collectTransactionContention: checked })}
                                                                disabled={uiState.disabled}
                                                        />

                                                        {formData.collectTransactionContention && (
                                                                <Alert
                                                                        message="Collecting Transaction Contention will involve some additional overhead. We therefore suggest that you enable this feature when you need to investigate a problem, but do not leave switched on all the time"
                                                                        heading="Collect Transaction Contention enabled"
                                                                        variant="alert-warning"
                                                                />
                                                        )}
                                                    </ConditionalRender>

                                                    {/* Deadlocks - Oracle and SQL Server only */}
                                                    {(formData.type === 'oracle' || formData.type === 'sqlserver') && (
                                                            <>
                                                                <CheckboxField
                                                                        id="collect-deadlocks"
                                                                        label="Collect Deadlocks"
                                                                        checked={formData.collectDeadlocks}
                                                                        onChange={(checked) => updateFormData({ collectDeadlocks: checked })}
                                                                        disabled={uiState.disabled}
                                                                />

                                                                {formData.collectDeadlocks && (
                                                                        <Alert
                                                                                message="Collecting Deadlock will involve some additional overhead. We therefore suggest that you enable this feature when you need to investigate a problem, but do not leave switched on all the time"
                                                                                heading="Collect Deadlocks enabled"
                                                                                variant="alert-warning"
                                                                        />
                                                                )}
                                                            </>
                                                    )}

                                                    {/* TempDB - SQL Server only */}
                                                    {formData.type === 'sqlserver' && (
                                                            <>
                                                                <CheckboxField
                                                                        id="collect-tempdb"
                                                                        label="Collect Tempdb"
                                                                        checked={formData.collectTempdb}
                                                                        onChange={(checked) => updateFormData({ collectTempdb: checked })}
                                                                        disabled={uiState.disabled}
                                                                />

                                                                {formData.collectTempdb && (
                                                                        <Alert
                                                                                message="Collecting Tempdb will involve some additional overhead. We therefore suggest that you enable this feature when you need to investigate a problem, but do not leave switched on all the time"
                                                                                heading="Collect Tempdb enabled"
                                                                                variant="alert-warning"
                                                                        />
                                                                )}
                                                            </>
                                                    )}

                                                    {/* Job History - SQL Server only */}
                                                    {formData.type === 'sqlserver' && (
                                                            <CheckboxField
                                                                    id="collect-job-history"
                                                                    label="Collect Job History"
                                                                    checked={formData.collectJobHistory}
                                                                    onChange={(checked) => updateFormData({ collectJobHistory: checked })}
                                                                    disabled={uiState.disabled}
                                                            />

                                                    )}
                                                    <CheckboxField
                                                            id="object-statistics"
                                                            label="Collect Object Statistics"
                                                            checked={formData.objectStatistics}
                                                            onChange={(checked) => updateFormData({ objectStatistics: checked })}
                                                            disabled={uiState.disabled}
                                                            tooltip="Object Statistics"
                                                    />

                                                    <CheckboxField
                                                            id="detect-changes"
                                                            label="Detect Changes"
                                                            checked={formData.detectChanges}
                                                            onChange={(checked) => updateFormData({ detectChanges: checked })}
                                                            disabled={uiState.disabled}
                                                            tooltip="Detect changes"
                                                    />

                                                    <CheckboxField
                                                            id="debug"
                                                            label="Debug Enabled"
                                                            checked={formData.debug}
                                                            onChange={(checked) => updateFormData({ debug: checked })}
                                                            disabled={uiState.disabled}
                                                            tooltip="Enable debug mode"
                                                    />

                                                    {formData.debug && (
                                                            <Alert
                                                                    message="We recommend only enabling debug mode when advised by DBmarlin technical support and for short periods only; otherwise, due to the additional data collected, it may fill your disk."
                                                                    heading="Debug mode enabled"
                                                                    variant="alert-warning"
                                                            />
                                                    )}
                                                </div>
                                        )}

                                        {/* Agent Selection */}
                                        <SelectField
                                                id="agentId"
                                                label="Agent"
                                                value={formData.agentId}
                                                onChange={(value) => updateFormData({ agentId: Number(value) })}
                                                options={dependencies.agents.map(agent => ({
                                                    value: agent.id,
                                                    label: `${agent.name} (${agent.hostname})`
                                                }))}
                                                required
                                                disabled={uiState.disabled}
                                                tooltip="The agent"
                                                className={validation.agentId.className}
                                        />

                                        {/* Host Mappings */}
                                        <div className="col-xs-12 col-md-3">
                                            <label htmlFor="mappings" className="reminder" data-tip="Map this instance to one or more hosts">
                                                Host Mappings
                                            </label>
                                            <span className="badge bg-info" data-tip="Total mapped host count">
                                        {formData.mappedHosts.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                    </span>
                                        </div>
                                        <div className="col-xs-12 col-md-6 col-lg-3">
                                            <div className="row g-0 mb-2">
                                                <div className="col-9">
                                                    <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Filter Instances"
                                                            value={formData.filter}
                                                            data-lpignore={true}
                                                            onChange={(e) => updateFormData({ filter: e.target.value.toLowerCase() })}
                                                    />
                                                </div>
                                                <div className="col-3 text-end">
                                                    <button type="button" className="btn btn-small btn-dark" onClick={clearFilter}>
                                                        <i className="fal fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="list-group">
                                                {hosts
                                                        .filter(item => item.name.toLowerCase().includes(formData.filter))
                                                        .sort((a, b) => a.name.localeCompare(b.name))
                                                        .slice(0, 7)
                                                        .map((host: { id: number; name: string; }) => (
                                                                <label key={host.id} className="list-group-item">
                                                                    <input
                                                                            className="form-check-input me-2"
                                                                            type="checkbox"
                                                                            value={host.id}
                                                                            checked={formData.mappedHosts.includes(host.id)}
                                                                            onChange={() => handleHostMapping(host.id)}
                                                                    />
                                                                    {host.name}
                                                                </label>
                                                        ))
                                                }
                                                {hosts.filter(item => item.name.toLowerCase().includes(formData.filter)).length > 7 && (
                                                        <label className="list-group-item text-black-50">
                                                            <small>Filter for more hosts...</small>
                                                        </label>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-100"/>

                                        {/* Third Party Links and Tags */}
                                        <ThirdPartyLinks
                                                links={formData.links}
                                                setLinks={(links: any) => updateFormData({ links })}
                                                instanceId={formData.id}
                                        />
                                        <Tagging
                                                tags={formData.tags}
                                                setTags={(tags: any) => updateFormData({ tags })}
                                                instanceId={formData.id}
                                                allTags={dependencies.allTags}
                                        />

                                        {/* Actions */}
                                        <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                            <button onClick={() => window.history.back()} className="btn btn-secondary">
                                                <i className="fal fa-times-circle fa-fw fa-sm"/>
                                                <span>Cancel</span>
                                            </button>
                                            <button
                                                    disabled={uiState.loading}
                                                    className={`btn btn-${action.variant}`}
                                                    onClick={handleSubmit}
                                            >
                                                <i className={`${action.icon} fa-fw fa-sm`}/>
                                                <span>{action.title}</span>
                                            </button>
                                        </div>
                                    </div>
                                </React.Fragment>
                        )}
                    </div>
                </div>
            </div>

            {/* Hidden button to trigger modal programmatically */}
            <button
                ref={modalTriggerRef}
                type="button"
                className="d-none"
                data-bs-toggle="modal"
                data-bs-target="#advancedModeConfirmModal"
                aria-hidden="true"
            />

            {/* Advanced Mode Confirmation Modal */}
            <div id="advancedModeConfirmModal" className="modal fade" tabIndex={-1} aria-labelledby="advancedModeConfirmModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="advancedModeConfirmModalLabel">
                                <i className="fal fa-exclamation-triangle fa-fw text-warning" aria-hidden="true" />
                                Disable Advanced Mode
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body p-3">
                            <p>This will revert your Connection String to the default built from the parameters above (Host, Port, etc.).</p>
                            <p>Are you sure you wish to proceed?</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                <i className="fal fa-times-circle fa-fw fa-sm" aria-hidden="true" />
                                <span>No</span>
                            </button>
                            <button type="button" className="btn btn-success" onClick={confirmDisableAdvanced} data-bs-dismiss="modal">
                                <i className="fal fa-check-circle fa-fw fa-sm" aria-hidden="true" />
                                <span>Yes</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Detail;
